import { REGEX_CONFIG } from 'app/shared/utils/regex-utils';
import { Section } from '../../../models';
import { ConfigureAlarmsFieldId, InstallationTypeSectionIds, PipeInstallationFieldIds, PipeInstallationMp2FieldIds } from '../../common.models';
import { LocationDetails } from 'app/shared/models/location-details';
import { pipeHeightWidthChange, setFieldValueAction } from '../../../actions';
import { field } from '../../../core/field-schema';
import { required, maxLength, pattern } from '../../../core/validator-builders';
import { TritonLocationCardState } from '../models';
import { Action } from '@ngrx/store';

const getHeightWidthValidators = (isMetric: boolean) => {
    const validators = [required(), maxLength({ value: 100 })];

    if (isMetric) {
        return [
            ...validators,
            pattern({
                value: REGEX_CONFIG.installationMetricValuePattern,
                message: 'COMMON.NUMERIC_PRECISION_ERROR_METRIC',
            }),
        ];
    }

    return [
        ...validators,
        pattern({
            value: REGEX_CONFIG.installationValuePattern,
            message: 'COMMON.NUMERIC_PRECISION_ERROR',
        }),
    ];
};

// Pipe installation type configuration (Series 200)
export const pipeInstallationConfig = (state: TritonLocationCardState, isMp1: boolean): Section => {
    const settings = state.settings;
    const customer = state.customer;
    let details: LocationDetails;

    if (state.locationDetails && state.locationDetails.length > 0) {
        details = isMp1 ? state.locationDetails[0] : state.locationDetails[1];
    }

    const FieldIds = isMp1 ? PipeInstallationFieldIds : PipeInstallationMp2FieldIds;

    return {
        info: {
            id: InstallationTypeSectionIds.Pipe,
            title: '',
            displayTitle: false,
        },
        rows: [
            {
                info: {
                    id: 'pipe-dimensions-row',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.number(FieldIds.Height, 'height', {
                        value: details ? details.height : null,
                        placeholder: 'HOME.MAP.MARKER_LOCATION_DETAIL.PIPE_HEIGHT',
                        placeholderSuffix: settings.depthUnit,
                        validators: getHeightWidthValidators(settings.isMetric),
                        panelClass: 'each-Item full-width required',
                        events: {
                            onChange: (value) => {
                                const actions: Action[] = [pipeHeightWidthChange({ isHeight: true, isMp1 })];

                                if (isMp1) {
                                    actions.push(
                                        setFieldValueAction({ fieldId: ConfigureAlarmsFieldId.FullPipeThreshold, value }),
                                    );
                                }

                                return actions;
                            },
                        },
                    }),
                    field.number(FieldIds.Width, 'width', {
                        value: details ? details.width : null,
                        placeholder: 'HOME.MAP.MARKER_LOCATION_DETAIL.PIPE_WIDTH',
                        placeholderSuffix: settings.depthUnit,
                        validators: getHeightWidthValidators(settings.isMetric),
                        panelClass: 'each-Item full-width required',
                        events: {
                            onChange: () => [pipeHeightWidthChange({ isHeight: false, isMp1 })],
                        },
                    }),
                ],
            },
            {
                info: {
                    id: 'pipe-properties-row',
                    panelClass: 'custom-50p pad-b-15',
                },
                items: [
                    field.number(FieldIds.Hc, 'hc', {
                        value: details ? details.designHC : 0,
                        placeholder: 'COMMON.HC',
                        validators: [
                            required(),
                            pattern({
                                value: REGEX_CONFIG.numericWith2DecimalPlaces,
                                message: 'COMMON.NUMERIC_PRECISION_ERROR_2_DECIMALS',
                            }),
                        ],
                        panelClass: 'each-Item full-width required',
                    }),
                    field.number(FieldIds.Slope, 'slope', {
                        value: details ? details.designSlope : 0,
                        placeholder: 'COMMON.SLOPE',
                        validators: [
                            required(),
                            pattern({
                                value: REGEX_CONFIG.numeric0_100With5DecimalPlacesPattern,
                                message: 'CALCULATED_ENTITY_DIALOG.INVALID_MANNING_SLOPE_CONSTANT_MESSAGE',
                            }),
                        ],
                        panelClass: 'each-Item full-width required',
                    }),
                ],
            },
            {
                info: {
                    id: 'pipe-advanced-properties-row',
                    panelClass: 'custom-50p pad-b-15',
                },
                items: [
                    field.number(FieldIds.Roughness, 'roughness', {
                        value: details ? (details.designRoughness ?? (customer?.roughness || 0)) : (customer?.roughness || 0),
                        placeholder: 'COMMON.ROUGHNESS',
                        validators: [
                            required(),
                            pattern({
                                value: REGEX_CONFIG.numericWith5DecimalPlaces,
                                message: 'COMMON.NUMERIC_PRECISION_ERROR_5_DECIMALS',
                            }),
                        ],
                        panelClass: 'each-Item full-width required',
                    }),
                    field.number(FieldIds.EffectiveRoughness, 'effectiveRoughness', {
                        value: details ? (details.effectiveRoughness ?? (customer?.roughness || 0)) : (customer?.roughness || 0),
                        placeholder: 'COMMON.EFFECTIVE_ROUGHNESS',
                        validators: [
                            required(),
                            pattern({
                                value: REGEX_CONFIG.numericWith5DecimalPlaces,
                                message: 'COMMON.NUMERIC_PRECISION_ERROR_5_DECIMALS',
                            }),
                        ],
                        panelClass: 'each-Item full-width required',
                    }),
                ],
            },
            {
                info: {
                    id: 'pipe-additional-properties-row',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.number(FieldIds.FrictionFactor, 'frictionFactor', {
                        value: details ? details.frictionFactor : 3,
                        visible: false,
                    }),
                    field.number(FieldIds.NominalDiameter, 'nominalDiameter', {
                        value: 0,
                        visible: false,
                    }),
                ],
            },
            {
                info: {
                    id: 'pipe-metadata-row',
                    panelClass: 'custom-50p',
                },
                items: [
                    field.date(FieldIds.StartDate, 'startDate', {
                        value: new Date(),
                        visible: false,
                    }),
                ],
            },
        ],
    };
};
